import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  deleteAttendanceEmployeeDayDetail,
  getAttendanceEmployeeDayDetail,
  resolveAttendanceUnusualSession,
  saveAttendanceEmployeeDayDetail,
} from "../api/asistenciaApi";
import { useEmpresa } from "../../../../app/providers/EmpresaProvider";

const ARGENTINA_TIME_ZONE = "America/Argentina/Buenos_Aires";
const UNUSUAL_MINUTES = 12 * 60;

function addDaysToYmd(ymd, amount) {
  if (!ymd) return "";
  const date = new Date(`${ymd}T12:00:00Z`);
  if (!Number.isFinite(date.getTime())) return ymd;
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

function automaticClockOutDate(session) {
  if (!session.clockInDate) return "";
  if (
    session.clockInTime &&
    session.clockOutTime &&
    session.clockOutTime < session.clockInTime
  ) {
    return addDaysToYmd(session.clockInDate, 1);
  }
  return session.clockInDate;
}

function localParts(value, fallbackDate = "") {
  if (!value) return { date: fallbackDate, time: "" };
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return { date: fallbackDate, time: "" };
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ARGENTINA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(parsed);
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${get("hour")}:${get("minute")}` };
}

function argentinaNow() {
  return localParts(new Date());
}

function newSession(ymd, branchId = "") {
  return {
    id: null,
    branchId,
    clockInDate: ymd,
    clockInTime: "",
    clockOutDate: ymd,
    clockOutTime: "",
    clockOutDateAuto: true,
    originalClockInAt: null,
    originalClockOutAt: null,
    isUnusualPending: false,
    unusualResolvedAt: null,
  };
}

function normalizeSessions(detail, ymd) {
  const branches = Array.isArray(detail?.assignableBranches) ? detail.assignableBranches : [];
  const defaultBranchId = branches.length === 1 ? Number(branches[0].id) : "";
  const rows = Array.isArray(detail?.sessions) ? detail.sessions : [];
  if (!rows.length) return [newSession(ymd, defaultBranchId)];

  return rows.map((session) => {
    const clockIn = localParts(session.clockInAt, ymd);
    const clockOut = localParts(session.clockOutAt, "");
    const suggestedClockOut = session.clockOutAt ? clockOut : argentinaNow();
    return {
      id: session.id ?? null,
      branchId: Number(session.branchId || defaultBranchId || 0) || "",
      clockInDate: clockIn.date,
      clockInTime: clockIn.time,
      clockOutDate: suggestedClockOut.date,
      clockOutTime: suggestedClockOut.time,
      clockOutDateAuto: false,
      originalClockInAt: session.clockInAt || null,
      originalClockOutAt: session.clockOutAt || null,
      isUnusualPending: Boolean(session.isUnusualPending),
      unusualResolvedAt: session.unusualResolvedAt || null,
    };
  });
}

function serializeState(sessions, deletedSessionIds) {
  return JSON.stringify({
    sessions: sessions.map((row) => ({
      id: row.id,
      branchId: row.branchId,
      clockInDate: row.clockInDate,
      clockInTime: row.clockInTime,
      clockOutDate: row.clockOutDate,
      clockOutTime: row.clockOutTime,
    })),
    deletedSessionIds: [...deletedSessionIds].sort((a, b) => a - b),
  });
}

function timestampFor(session, kind) {
  const date = session[`${kind}Date`];
  const time = session[`${kind}Time`];
  if (kind === "clockOut" && !time) return null;
  const originalKey = kind === "clockIn" ? "originalClockInAt" : "originalClockOutAt";
  const original = session[originalKey];
  const originalParts = localParts(original);
  if (original && originalParts.date === date && originalParts.time === time) return original;
  return `${date}T${time}:00-03:00`;
}

function durationMinutes(session, now, allowOpen = false) {
  if (!session.clockInDate || !session.clockInTime) return null;
  const start = new Date(`${session.clockInDate}T${session.clockInTime}:00-03:00`).getTime();
  const hasOutput = Boolean(session.clockOutDate && session.clockOutTime);
  if (!hasOutput && !allowOpen) return null;
  const end = hasOutput
    ? new Date(`${session.clockOutDate}T${session.clockOutTime}:00-03:00`).getTime()
    : now;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return Math.floor((end - start) / 60000);
}

function formatDuration(minutes) {
  if (minutes == null) return "—";
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

function validateSessions(sessions, deletedSessionIds, hadSessions) {
  if (!sessions.length) {
    return hadSessions && deletedSessionIds.length ? "" : "Agregá al menos un turno.";
  }
  const intervals = [];
  for (const session of sessions) {
    if (!session.branchId) return "Seleccioná una sucursal en todos los turnos.";
    if (!session.clockInDate || !session.clockInTime) return "Completá fecha y hora de entrada.";
    const hasOutDate = Boolean(session.clockOutDate);
    const hasOutTime = Boolean(session.clockOutTime);
    if (hasOutTime && !hasOutDate) return "Completá la fecha de salida.";
    const start = new Date(`${session.clockInDate}T${session.clockInTime}:00-03:00`).getTime();
    const end = hasOutTime
      ? new Date(`${session.clockOutDate}T${session.clockOutTime}:00-03:00`).getTime()
      : Number.POSITIVE_INFINITY;
    if (!Number.isFinite(start) || (!Number.isFinite(end) && end !== Number.POSITIVE_INFINITY)) {
      return "Ingresá fechas y horarios válidos.";
    }
    if (end <= start) return "La salida debe ser posterior a la entrada.";
    intervals.push({ start, end });
  }
  intervals.sort((a, b) => a.start - b.start);
  for (let index = 0; index < intervals.length - 1; index += 1) {
    if (intervals[index].end > intervals[index + 1].start) {
      return "Los turnos no pueden superponerse.";
    }
  }
  return "";
}

export default function AttendanceDayDetailModal({ open, row, employeeName, onClose, onSaved }) {
  const { empresaId } = useEmpresa();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [deletedSessionIds, setDeletedSessionIds] = useState([]);
  const [initialSerialized, setInitialSerialized] = useState("");
  const [assignableBranches, setAssignableBranches] = useState([]);
  const [hadSessions, setHadSessions] = useState(false);
  const [warningSessionId, setWarningSessionId] = useState(null);
  const [now, setNow] = useState(Date.now());

  const employeeId = Number(row?.employeeId || row?.id || 0);
  const ymd = String(row?.ymd || "").slice(0, 10);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (!open || !employeeId || !ymd) return undefined;
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError("");
        setNotice("");
        setShowValidation(false);
        const detail = await getAttendanceEmployeeDayDetail(
          { licenseId: empresaId || undefined, employeeId, date: ymd },
          { signal: ac.signal }
        );
        const normalized = normalizeSessions(detail, ymd);
        setSessions(normalized);
        setDeletedSessionIds([]);
        setInitialSerialized(serializeState(normalized.map((session) => (
          session.originalClockOutAt
            ? session
            : { ...session, clockOutDate: "", clockOutTime: "" }
        )), []));
        setAssignableBranches(Array.isArray(detail?.assignableBranches) ? detail.assignableBranches : []);
        setHadSessions(Boolean(detail?.sessions?.length));
        setWarningSessionId(detail?.sessions?.find((item) => item.isUnusualPending)?.id ?? null);
      } catch (err) {
        if (err?.name !== "AbortError") setError(err?.message || "No se pudo cargar el detalle del día.");
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [open, employeeId, ymd, empresaId]);

  const currentSerialized = serializeState(sessions, deletedSessionIds);
  const isDirty = currentSerialized !== initialSerialized;
  const validationError = useMemo(
    () => validateSessions(sessions, deletedSessionIds, hadSessions),
    [sessions, deletedSessionIds, hadSessions]
  );

  const change = (index, field, value) => {
    setError("");
    setNotice("");
    const changesTimestamp = ["clockInDate", "clockInTime", "clockOutDate", "clockOutTime"].includes(field);
    setSessions((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;

      const next = {
        ...item,
        [field]: value,
        ...(changesTimestamp ? { isUnusualPending: false, unusualResolvedAt: null } : {}),
      };

      if (field === "clockOutDate") {
        next.clockOutDateAuto = false;
      } else if (
        next.clockOutDateAuto &&
        ["clockInDate", "clockInTime", "clockOutTime"].includes(field)
      ) {
        next.clockOutDate = automaticClockOutDate(next);
      }

      return next;
    }));
  };

  const remove = (index) => {
    const target = sessions[index];
    if (target?.id) setDeletedSessionIds((ids) => [...new Set([...ids, Number(target.id)])]);
    setSessions((items) => items.filter((_, itemIndex) => itemIndex !== index));
  };

  const applySavedDetail = (detail) => {
    const normalized = normalizeSessions(detail, ymd);
    setSessions(normalized);
    setDeletedSessionIds([]);
    setInitialSerialized(serializeState(normalized, []));
    setHadSessions(Boolean(detail?.sessions?.length));
  };

  const handleSave = async () => {
    setShowValidation(true);
    if (!isDirty || validationError || saving || deleting) return;
    try {
      setSaving(true);
      setError("");
      const result = await saveAttendanceEmployeeDayDetail({
        licenseId: empresaId || undefined,
        employeeId,
        date: ymd,
        sessions: sessions.map((session) => ({
          ...(session.id ? { id: Number(session.id) } : {}),
          branchId: Number(session.branchId),
          clockInAt: timestampFor(session, "clockIn"),
          clockOutAt: timestampFor(session, "clockOut"),
        })),
        deletedSessionIds,
      });
      if (result?.payrollImpact?.requiresReconsolidation) {
        applySavedDetail(result);
        setNotice("La asistencia se guardó. Hay una liquidación en borrador: volvé a consolidarla para actualizar sus cálculos.");
        onSaved?.({ keepOpen: true });
      } else {
        onSaved?.();
        onClose?.();
      }
    } catch (err) {
      setError(err?.message || "No se pudo guardar la asistencia.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!hadSessions || !window.confirm("¿Borrar toda la asistencia consultada? Se eliminarán sus entradas y salidas.")) return;
    try {
      setDeleting(true);
      setError("");
      const result = await deleteAttendanceEmployeeDayDetail({
        licenseId: empresaId || undefined,
        employeeId,
        date: ymd,
      });
      if (result?.payrollImpact?.requiresReconsolidation) {
        const defaultBranch = assignableBranches.length === 1 ? Number(assignableBranches[0].id) : "";
        const empty = [newSession(ymd, defaultBranch)];
        setSessions(empty);
        setDeletedSessionIds([]);
        setInitialSerialized(serializeState(empty, []));
        setHadSessions(false);
        setNotice("La asistencia se borró. Hay una liquidación en borrador que debe volver a consolidarse.");
        onSaved?.({ keepOpen: true });
      } else {
        onSaved?.();
        onClose?.();
      }
    } catch (err) {
      setError(err?.message || "No se pudo borrar la asistencia.");
    } finally {
      setDeleting(false);
    }
  };

  const handleResolve = async () => {
    if (!warningSessionId) return;
    try {
      setResolving(true);
      setError("");
      await resolveAttendanceUnusualSession(warningSessionId, {
        licenseId: empresaId || undefined,
        employeeId,
        date: ymd,
      });
      setSessions((items) => items.map((item) => (
        Number(item.id) === Number(warningSessionId)
          ? { ...item, isUnusualPending: false, unusualResolvedAt: new Date().toISOString() }
          : item
      )));
      const nextPending = sessions.find((item) => (
        Number(item.id) !== Number(warningSessionId) && item.isUnusualPending
      ));
      setWarningSessionId(nextPending?.id ?? null);
      onSaved?.({ keepOpen: true });
    } catch (err) {
      setError(err?.message || "No se pudo marcar la advertencia como resuelta.");
    } finally {
      setResolving(false);
    }
  };

  const titleDate = ymd ? ymd.split("-").reverse().join("/") : "";
  const busy = loading || saving || deleting || resolving;
  const completedMinutes = sessions.reduce((total, session) => (
    session.clockOutDate && session.clockOutTime
      ? total + Math.max(0, durationMinutes(session, now) || 0)
      : total
  ), 0);
  const warningSession = sessions.find((item) => Number(item.id) === Number(warningSessionId));
  const warningMinutes = warningSession
    ? durationMinutes(
        warningSession,
        now,
        Boolean(warningSession.originalClockInAt && !warningSession.originalClockOutAt)
      )
    : null;
  const displayDateTime = (date, time) => (
    date && time ? `${date.split("-").reverse().join("/")} ${time}` : "En curso"
  );

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: "24px", bgcolor: "neutral.200" } }}>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 900, color: "primary.900" }}>
            Asistencia — {employeeName || row?.fullName || "Empleado"}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "neutral.700" }}>
            Fecha consultada: {titleDate}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.5}>
            {loading ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography>Cargando turnos...</Typography></Stack> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
            {notice ? <Alert severity="warning">{notice}</Alert> : null}
            {!error && showValidation && validationError ? <Alert severity="warning">{validationError}</Alert> : null}
            {!loading ? (
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>
                Total de sesiones finalizadas: {formatDuration(completedMinutes)}
              </Typography>
            ) : null}

            {sessions.map((session, index) => {
              const hasOutput = Boolean(session.clockOutDate && session.clockOutTime);
              const isOpen = !hasOutput;
              const isPersistedOpen = Boolean(
                session.id && session.originalClockInAt && !session.originalClockOutAt && !hasOutput
              );
              const minutes = durationMinutes(session, now, isPersistedOpen);
              const isPending = session.isUnusualPending || (minutes > UNUSUAL_MINUTES && !session.unusualResolvedAt);
              return (
                <Box key={`${session.id || "new"}-${index}`} sx={{ p: 1.5, borderRadius: "14px", bgcolor: "neutral.50", border: "1px solid", borderColor: isPending ? "warning.main" : "rgba(96,96,113,0.18)" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontWeight: 900 }}>Turno {index + 1}</Typography>
                      <Chip size="small" label={isOpen ? "En curso" : "Finalizada"} color={isOpen ? "info" : "success"} />
                      {isPending ? <Chip size="small" icon={<WarningAmberIcon />} label="Jornada inusual" color="warning" /> : null}
                    </Stack>
                    <Tooltip title="Eliminar turno"><IconButton size="small" onClick={() => remove(index)} disabled={busy}><DeleteOutlineIcon /></IconButton></Tooltip>
                  </Stack>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "76px minmax(160px, 230px) minmax(140px, 190px)" }, gap: 1.25, alignItems: "center" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: "neutral.700" }}>Entrada</Typography>
                    <TextField label="Fecha" type="date" size="small" value={session.clockInDate} onChange={(e) => change(index, "clockInDate", e.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField label="Hora" type="time" size="small" value={session.clockInTime} onChange={(e) => change(index, "clockInTime", e.target.value)} InputLabelProps={{ shrink: true }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: "neutral.700" }}>Salida</Typography>
                    <TextField label="Fecha" type="date" size="small" value={session.clockOutDate} onChange={(e) => change(index, "clockOutDate", e.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField label="Hora (opcional)" type="time" size="small" value={session.clockOutTime} onChange={(e) => change(index, "clockOutTime", e.target.value)} InputLabelProps={{ shrink: true }} />
                    <Box sx={{ display: { xs: "none", sm: "block" } }} />
                    <TextField select label="Sucursal" size="small" value={session.branchId || ""} onChange={(e) => change(index, "branchId", e.target.value)} InputLabelProps={{ shrink: true }}>
                      {assignableBranches.map((branch) => <MenuItem key={branch.id} value={Number(branch.id)}>{branch.name}</MenuItem>)}
                    </TextField>
                    <TextField label="Duración calculada" size="small" value={`${formatDuration(minutes)}${isPersistedOpen ? " (provisional)" : ""}`} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                  </Box>
                  {isPending ? (
                    <Alert
                      sx={{ mt: 1.2 }}
                      severity="warning"
                      action={session.id ? <Button color="inherit" size="small" onClick={() => setWarningSessionId(session.id)}>Revisar</Button> : null}
                    >
                      Esta jornada supera las 12 horas. Revisá los horarios o marcá la advertencia como resuelta.
                    </Alert>
                  ) : null}
                </Box>
              );
            })}

            <Button startIcon={<AddIcon />} onClick={() => setSessions((items) => [...items, newSession(ymd, assignableBranches.length === 1 ? Number(assignableBranches[0].id) : "")])} disabled={busy} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 800 }}>
              Agregar turno
            </Button>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          <Button onClick={handleDeleteAll} disabled={!hadSessions || busy} color="error" sx={{ textTransform: "none", fontWeight: 900 }}>
            {deleting ? "Borrando..." : "Borrar asistencia"}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 800 }}>Cerrar</Button>
            <Button onClick={handleSave} disabled={!isDirty || busy} variant="contained" sx={{ textTransform: "none", fontWeight: 900 }}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(open && warningSessionId)} onClose={resolving ? undefined : () => setWarningSessionId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", gap: 1, alignItems: "center" }}><WarningAmberIcon color="warning" /> Turno inusual</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>Esta jornada supera las 12 horas. Verificá las fechas y horas registradas.</Typography>
            {warningSession ? (
              <Box sx={{ p: 1.25, borderRadius: "10px", bgcolor: "rgba(245, 124, 0, 0.10)" }}>
                <Typography sx={{ fontSize: 13 }}><strong>Entrada:</strong> {displayDateTime(warningSession.clockInDate, warningSession.clockInTime)}</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>Salida:</strong> {displayDateTime(warningSession.clockOutDate, warningSession.clockOutTime)}</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>Duración:</strong> {formatDuration(warningMinutes)}{!warningSession.clockOutTime ? " (provisional)" : ""}</Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningSessionId(null)} disabled={resolving}>Editar asistencia</Button>
          <Button variant="contained" color="warning" onClick={handleResolve} disabled={resolving}>{resolving ? "Resolviendo..." : "Marcar como resuelta"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
